import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { formatEatDateTime } from '@wodi/domain';
import type { AdmissionView, ConsultationView, WorkspaceView } from '@wodi/shared';
import { Button, LoadingBlock, Notice, PageHeader, Section, Tag } from '@wodi/ui';
import { useRef } from 'react';

import { workspaceQuery } from '../api/consultation.ts';
import { admissionQuery } from '../api/inpatient.ts';
import { startWardRound } from '../api/nursing.ts';
import { can, sessionQuery } from '../api/session.ts';
import { DiagnosesPanel } from '../consultation/diagnoses-panel.tsx';
import { FinishPanel } from '../consultation/finish-panel.tsx';
import { NotePanel } from '../consultation/note-panel.tsx';
import { OrdersPanel } from '../consultation/orders-panel.tsx';
import { SummaryPanel, VitalsToday } from '../consultation/summary-panel.tsx';
import { WORKSPACE_PANELS } from '../consultation/workspace-panels.tsx';
import { useOnline } from '../hooks/use-online.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { toasts } from '../toasts.ts';
import { errorText } from './bed-board-page.tsx';
import styles from './nursing.module.css';

// A ward round (docs/plans/phase2-ward-rounds-nursing.md §5): the consultation workspace's
// own panels on the stay's inpatient visit. Each round is a consultation of kind
// `ward_round`, so the note, diagnoses, orders and prescriptions are written and checked by
// the same code as in outpatients. No "Close visit" and no queue step: discharge closes an
// inpatient visit.

const route = getRouteApi('/_app/admissions/$admissionId/ward-round');

function roundTitle(c: ConsultationView): string {
  return `${formatEatDateTime(new Date(c.startedAt))} · ${c.clinician.displayName}${c.ward === null ? '' : ` · ${c.ward.name}`}`;
}

const ROUND_TAGS: Record<ConsultationView['status'], [string, 'active' | 'done' | 'neutral']> = {
  in_progress: ['In progress', 'active'],
  finished: ['Finished', 'done'],
  entered_in_error: ['Entered in error', 'neutral'],
};

function Rounds({ view, mayRecord }: { view: WorkspaceView; mayRecord: boolean }) {
  const rounds = view.consultations.filter((c) => c.kind === 'ward_round').reverse();
  if (rounds.length === 0) {
    return (
      <Section title="Ward rounds">
        <p className={styles.meta}>
          {mayRecord ? 'No ward round yet. Start one to write the note.' : 'No ward round yet.'}
        </p>
      </Section>
    );
  }
  return (
    <Section
      title="Ward rounds"
      description="Newest first. Signed notes are amended with a reason, never deleted."
    >
      {rounds.map((c) => {
        const [word, state] = ROUND_TAGS[c.status];
        return (
          <article key={c.id} className={styles.round} aria-label={`Ward round ${roundTitle(c)}`}>
            <div className={styles.actions}>
              <h3 className={styles.roundHeading}>{roundTitle(c)}</h3>
              <Tag state={state}>{word}</Tag>
            </div>
            <NotePanel view={view} consultationId={c.id} mayWrite={mayRecord} />
          </article>
        );
      })}
    </Section>
  );
}

function place(stay: AdmissionView): string {
  const c = stay.current;
  if (c === null) return `Day ${String(stay.daysAdmitted)}.`;
  return `${c.ward.name}${c.bed === null ? ', waiting for a bed' : `, bed ${c.bed.label}`}. Day ${String(stay.daysAdmitted)}.`;
}

function Workspace({ stay }: { stay: AdmissionView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const workspace = useQuery(workspaceQuery(stay.visit.id));
  const requestId = useRef(crypto.randomUUID());
  const start = useMutation({
    mutationFn: () => startWardRound(stay.id, requestId.current),
    onSuccess: async () => {
      requestId.current = crypto.randomUUID();
      await queryClient.invalidateQueries({ queryKey: ['visits', stay.visit.id, 'workspace'] });
      await queryClient.invalidateQueries({ queryKey: ['inpatient', 'worklist'] });
      toasts.show({ title: 'Ward round started' });
    },
  });

  if (workspace.isPending) return <LoadingBlock label="Loading the ward round" rows={8} />;
  if (workspace.isError) {
    return (
      <Notice
        tone="error"
        action={<Button onPress={() => void workspace.refetch()}>Try again</Button>}
      >
        {errorText(workspace.error, 'The ward round could not be loaded. Try again.')}
      </Notice>
    );
  }
  const view = workspace.data;
  const mayRecord = session !== null && can(session, 'consultation.record');
  const admitted = stay.status === 'admitted' && view.visit.status === 'open';
  const mine = view.consultations.find((c) => c.id === view.myConsultationId);

  return (
    <>
      <PageHeader
        title="Ward round"
        description={place(stay)}
        actions={
          <>
            {mine === undefined && mayRecord && admitted ? (
              <Button
                variant="primary"
                isDisabled={!online}
                isPending={start.isPending}
                pendingLabel="Starting…"
                onPress={() => {
                  start.mutate();
                }}
              >
                Start ward round
              </Button>
            ) : null}
            <Link to="/admissions/$admissionId/nursing" params={{ admissionId: stay.id }}>
              Nursing
            </Link>
            <Link to="/admissions/$admissionId" params={{ admissionId: stay.id }}>
              Back to the stay
            </Link>
          </>
        }
      />
      {start.error === null ? null : (
        <Notice tone="error">
          {errorText(start.error, 'The ward round could not be started.')}
        </Notice>
      )}
      {!online ? (
        <Notice tone="info">
          No connection to Wodi. Your note keeps saving on this device; signing, ordering and
          finishing wait for the connection.
        </Notice>
      ) : null}
      {admitted ? null : (
        <Notice tone="info">
          This stay has ended. Its ward rounds are shown; nothing new can be added.
        </Notice>
      )}
      <VitalsToday view={view} />
      <SummaryPanel
        view={view}
        mayWrite={mine !== undefined && mayRecord}
        mayVerify={session !== null && can(session, 'allergies.verify')}
      />
      <div className={styles.columns}>
        <div className={styles.column}>
          <Rounds view={view} mayRecord={mayRecord} />
          {mine !== undefined && mayRecord ? (
            <FinishPanel
              view={view}
              consultationId={mine.id}
              inProgress={mine.status === 'in_progress'}
              noun="Ward round"
            />
          ) : null}
        </div>
        <div
          className={styles.column}
          aria-label="Diagnoses, orders and prescriptions"
          role="region"
        >
          <DiagnosesPanel view={view} mayWrite={mayRecord && admitted} />
          <OrdersPanel view={view} consultationId={mine?.id ?? null} />
          <WORKSPACE_PANELS.prescribing view={view} consultationId={mine?.id ?? null} />
          {/* Sick sheets, referral letters and uploads, as in the consultation
              (docs/plans/phase2-referrals.md §6: referring out from a ward round). */}
          {WORKSPACE_PANELS.documents === null ? null : (
            <WORKSPACE_PANELS.documents view={view} consultationId={mine?.id ?? null} />
          )}
        </div>
      </div>
    </>
  );
}

export function WardRoundPage() {
  const { admissionId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const stay = useQuery(admissionQuery(admissionId));
  if (session === null || !can(session, 'clinical.read') || !can(session, 'inpatient.read')) {
    return (
      <Notice tone="info">
        You don't have permission to see ward rounds. Ask an administrator if you need it.
      </Notice>
    );
  }
  if (stay.isPending) return <LoadingBlock label="Loading the stay" rows={6} />;
  if (stay.isError) {
    return (
      <Notice tone="error" action={<Button onPress={() => void stay.refetch()}>Try again</Button>}>
        {errorText(stay.error, "The stay couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={stay.data.patientId} />
      <Workspace stay={stay.data} />
    </div>
  );
}
